import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  ArrowLeft,
  Download,
  Clock,
  User,
  Building,
  IndianRupee,
  PenTool,
  Shield,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EmbeddedSigningPage = () => {
  const { investmentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMock = searchParams.get('mock') === 'true';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signingData, setSigningData] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signatureComplete, setSignatureComplete] = useState(false);
  const [showFullDocument, setShowFullDocument] = useState(false);
  
  // Mock signing state
  const [mockSigningStep, setMockSigningStep] = useState(0); // 0: ready, 1: reviewing, 2: signing, 3: complete
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signatureDrawn, setSignatureDrawn] = useState(false);

  const token = localStorage.getItem('token');

  // Fetch embedded signing details
  const fetchSigningData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get embedded signing URL
      const response = await fetch(`${API}/api/documents/embedded-signing/${investmentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to load signing data');
      }

      const data = await response.json();
      
      if (data.already_signed) {
        setSignatureComplete(true);
        setSigningData(data);
        return;
      }
      
      setSigningData(data);

      // Also fetch document preview
      const previewResponse = await fetch(`${API}/api/documents/preview/${investmentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (previewResponse.ok) {
        const previewData = await previewResponse.json();
        setDocumentPreview(previewData.document);
      }

    } catch (err) {
      console.error('Error fetching signing data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [investmentId, token]);

  useEffect(() => {
    fetchSigningData();
  }, [fetchSigningData]);

  // Handle mock signing completion
  const handleMockSigning = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setSigning(true);
    setMockSigningStep(2);

    // Simulate signing delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      // Call the complete endpoint with skip_zoho_verify for mock mode
      const skipVerify = isMock || signingData?.is_mock || signingData?.is_fallback;
      const response = await fetch(`${API}/api/documents/embedded-signing/${investmentId}/complete?skip_zoho_verify=${skipVerify}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to complete signing');
      }

      const data = await response.json();
      
      setMockSigningStep(3);
      setSignatureComplete(true);
      setSignatureDrawn(true);
      toast.success('Document signed successfully!');
      
    } catch (err) {
      console.error('Error completing signing:', err);
      toast.error(err.message || 'Failed to complete signing');
      setMockSigningStep(1);
    } finally {
      setSigning(false);
    }
  };

  // Navigate back to portfolio
  const handleBack = () => {
    navigate('/investor?tab=portfolio');
  };

  // Navigate to capital payment
  const handleProceedToPayment = () => {
    navigate(`/invest/${signingData?.unit_id || investmentId}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center" data-testid="signing-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-white text-lg">Loading document...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4" data-testid="signing-error">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Document</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={handleBack} variant="outline" className="w-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Portfolio
          </Button>
        </div>
      </div>
    );
  }

  // Already signed state
  if (signatureComplete && mockSigningStep !== 3) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4" data-testid="signing-complete">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Document Already Signed</h2>
          <p className="text-gray-600 mb-6">
            This LLP Agreement has already been signed. You can proceed to make the capital payment.
          </p>
          <div className="space-y-3">
            <Button onClick={handleProceedToPayment} className="w-full bg-green-600 hover:bg-green-700">
              Proceed to Capital Payment
            </Button>
            <Button onClick={handleBack} variant="outline" className="w-full">
              Back to Portfolio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Successful signing completion
  if (mockSigningStep === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4" data-testid="signing-success">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Document Signed Successfully!</h2>
            <p className="text-gray-600">
              Your LLP Subscription Agreement has been signed electronically. 
              A copy will be sent to your registered email.
            </p>
          </div>

          {/* Summary Card */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-6 border border-green-200">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Signing Summary
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Document</span>
                <span className="font-medium">LLP Subscription Agreement</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Signed By</span>
                <span className="font-medium">{documentPreview?.investor?.name || 'Investor'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date</span>
                <span className="font-medium">{new Date().toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Investment ID</span>
                <span className="font-mono text-xs">{investmentId}</span>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-blue-50 rounded-xl p-6 mb-6 border border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Next Step
            </h3>
            <p className="text-sm text-gray-700 mb-4">
              Complete your investment by paying the remaining capital amount of{' '}
              <span className="font-bold text-blue-700">
                ₹{(documentPreview?.investment?.capital_amount || 0).toLocaleString('en-IN')}
              </span>
            </p>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={handleProceedToPayment} 
              className="w-full bg-blue-600 hover:bg-blue-700 h-12"
              data-testid="proceed-to-payment-btn"
            >
              <IndianRupee className="w-4 h-4 mr-2" />
              Pay Capital Amount
            </Button>
            <Button onClick={handleBack} variant="outline" className="w-full">
              Back to Portfolio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main signing interface (Mock Mode)
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800" data-testid="embedded-signing-page">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Secure Signing
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Column - Document Preview */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Document Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-8 h-8" />
                  <div>
                    <h1 className="text-xl font-bold">LLP Subscription Agreement</h1>
                    <p className="text-blue-100 text-sm">{documentPreview?.llp?.name || 'Property Investment LLP'}</p>
                  </div>
                </div>
              </div>

              {/* Document Content Preview */}
              <div className="p-6">
                {/* Key Terms Summary */}
                <div className="mb-6">
                  <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Agreement Summary
                  </h2>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <InfoCard
                      icon={<User className="w-5 h-5 text-blue-600" />}
                      label="Subscriber"
                      value={documentPreview?.investor?.name || 'Loading...'}
                    />
                    <InfoCard
                      icon={<Building className="w-5 h-5 text-purple-600" />}
                      label="Property"
                      value={documentPreview?.unit?.name || documentPreview?.project?.name || 'Loading...'}
                    />
                    <InfoCard
                      icon={<IndianRupee className="w-5 h-5 text-green-600" />}
                      label="Investment Amount"
                      value={`₹${(documentPreview?.investment?.total_commitment || 0).toLocaleString('en-IN')}`}
                    />
                    <InfoCard
                      icon={<PenTool className="w-5 h-5 text-amber-600" />}
                      label="Ownership Stake"
                      value={`${documentPreview?.investment?.shareholding_percent || 0}%`}
                    />
                  </div>
                </div>

                {/* Expandable Document Terms */}
                <div className="border rounded-xl overflow-hidden">
                  <button
                    onClick={() => setShowFullDocument(!showFullDocument)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <span className="font-medium text-gray-900">View Full Agreement Terms</span>
                    {showFullDocument ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  
                  {showFullDocument && (
                    <div className="p-6 border-t bg-white max-h-96 overflow-y-auto">
                      <div className="prose prose-sm max-w-none">
                        <h3 className="text-lg font-bold">LLP SUBSCRIPTION AGREEMENT</h3>
                        <p className="text-sm text-gray-600">
                          Date: {documentPreview?.agreement_date || new Date().toLocaleDateString('en-IN')}
                        </p>
                        
                        <h4>1. PARTIES</h4>
                        <p>
                          This Agreement is entered into between:<br/>
                          <strong>(1) {documentPreview?.llp?.name || 'Property Investment LLP'}</strong> 
                          (Registration No: {documentPreview?.llp?.registration_number || 'PENDING'}), 
                          hereinafter referred to as &quot;LLP&quot;<br/>
                          AND<br/>
                          <strong>(2) {documentPreview?.investor?.name || 'Investor'}</strong> 
                          (PAN: {documentPreview?.investor?.pan || 'XXXXX0000X'}), 
                          hereinafter referred to as &quot;Subscriber&quot;
                        </p>

                        <h4>2. SUBSCRIPTION DETAILS</h4>
                        <ul>
                          <li>Total Subscription Amount: ₹{(documentPreview?.investment?.total_commitment || 0).toLocaleString('en-IN')}</li>
                          <li>Expression of Interest (EOI) Paid: ₹{(documentPreview?.investment?.eoi_amount || 0).toLocaleString('en-IN')}</li>
                          <li>Balance Capital Payable: ₹{(documentPreview?.investment?.capital_amount || 0).toLocaleString('en-IN')}</li>
                          <li>Ownership Percentage: {documentPreview?.investment?.shareholding_percent || 0}%</li>
                        </ul>

                        <h4>3. PROPERTY DETAILS</h4>
                        <p>
                          The investment relates to the following property:<br/>
                          <strong>Unit:</strong> {documentPreview?.unit?.name || 'N/A'}<br/>
                          <strong>Project:</strong> {documentPreview?.project?.name || 'N/A'}
                        </p>

                        <h4>4. TERMS AND CONDITIONS</h4>
                        <ol>
                          <li>The Subscriber agrees to contribute the subscription amount to the LLP.</li>
                          <li>The LLP will use the contributed capital solely for property acquisition.</li>
                          <li>Profit and loss sharing will be in proportion to ownership percentage.</li>
                          <li>Exit mechanisms include secondary sale, buyback, or property liquidation.</li>
                          <li>This agreement is governed by Indian law.</li>
                        </ol>

                        <h4>5. REPRESENTATIONS</h4>
                        <p>
                          The Subscriber represents that they are of legal age, have valid PAN and Aadhaar, 
                          and the source of funds is legitimate.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Signing Panel */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24">
              <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <PenTool className="w-5 h-5 text-blue-600" />
                Sign Document
              </h2>

              {/* Signing Steps */}
              <div className="space-y-4 mb-6">
                <StepItem 
                  number={1} 
                  title="Review Agreement" 
                  description="Read and understand all terms"
                  active={mockSigningStep === 0 || mockSigningStep === 1}
                  completed={mockSigningStep > 1}
                />
                <StepItem 
                  number={2} 
                  title="Accept Terms" 
                  description="Agree to the conditions"
                  active={mockSigningStep === 1}
                  completed={mockSigningStep > 1}
                />
                <StepItem 
                  number={3} 
                  title="Sign Document" 
                  description="Apply your electronic signature"
                  active={mockSigningStep === 2}
                  completed={mockSigningStep === 3}
                />
              </div>

              {/* Terms Acceptance */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      if (e.target.checked) setMockSigningStep(1);
                    }}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="terms-checkbox"
                  />
                  <span className="text-sm text-gray-700">
                    I have read and agree to the terms of the LLP Subscription Agreement. 
                    I understand that this is a legally binding document.
                  </span>
                </label>
              </div>

              {/* Sign Button */}
              <Button
                onClick={handleMockSigning}
                disabled={!termsAccepted || signing}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
                data-testid="sign-document-btn"
              >
                {signing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    <PenTool className="w-5 h-5 mr-2" />
                    Sign Document
                  </>
                )}
              </Button>

              {/* Security Notice */}
              <div className="mt-6 flex items-start gap-3 text-xs text-gray-500">
                <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  Your signature will be securely recorded with a timestamp and IP address. 
                  This creates a legally binding electronic signature under the IT Act 2000.
                </p>
              </div>

              {/* Mock Mode Notice */}
              {(isMock || signingData?.is_mock) && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>
                      <strong>Demo Mode:</strong> This is a simulated signing experience. 
                      Real signatures will use Zoho Sign.
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const InfoCard = ({ icon, label, value }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
      {icon}
      <span>{label}</span>
    </div>
    <p className="font-semibold text-gray-900">{value}</p>
  </div>
);

const StepItem = ({ number, title, description, active, completed }) => (
  <div className={`flex items-start gap-3 p-3 rounded-lg transition-all ${
    active ? 'bg-blue-50 border border-blue-200' : 
    completed ? 'bg-green-50 border border-green-200' : 
    'bg-gray-50'
  }`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
      completed ? 'bg-green-500 text-white' :
      active ? 'bg-blue-500 text-white' : 
      'bg-gray-300 text-gray-600'
    }`}>
      {completed ? <CheckCircle className="w-5 h-5" /> : number}
    </div>
    <div>
      <p className={`font-medium ${active ? 'text-blue-900' : completed ? 'text-green-900' : 'text-gray-700'}`}>
        {title}
      </p>
      <p className={`text-sm ${active ? 'text-blue-600' : completed ? 'text-green-600' : 'text-gray-500'}`}>
        {description}
      </p>
    </div>
  </div>
);

export default EmbeddedSigningPage;
